import React from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Chip, CircularProgress } from '@mui/material'
import { IconButton, Tooltip } from '@mui/material'
import { useFilterStore } from '../../stores/useFilterStore'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { getTopicName } from '../../hooks/utils'
import { useZustand } from '../../hooks/useZustand'

const SelectedFilters = ({ disabled }) => {
    const recalculateFilters = useFilterStore(state => state.recalculate)
    const highlightFilters = useFilterStore(state => state.highlight)
    const isAnyFilterActive = useFilterStore(state => state.isAnyFilterActive())

    const setHighlight = useFilterStore(state => state.setHighlight)
    const setRecalculate = useFilterStore(state => state.setRecalculate)
    const resetAllFilters = useFilterStore(state => state.resetAllFilters)

    const activeFilterCount = useFilterStore(state => state.activeFilterCount())

    const topics = useZustand(state => state.topics)
    // While Google sentiment analysis runs (~30s), show a placeholder chip with
    // a spinner in place of the sentiment chip; it's replaced by the real chip
    // once the analysis completes and the filter is applied.
    const isAnalyzingSentiment = useZustand(
        state => state.googleSentimentStatus === 'loading'
    )

    const handleDelete = (type, category, value) => {
        if (type === 'recalculate') {
            setRecalculate({
                ...recalculateFilters,
                [category]: recalculateFilters[category].filter(
                    item => item !== value
                ),
            })
            return
        } else if (type === 'highlight') {
            setHighlight({
                ...highlightFilters,
                [category]: highlightFilters[category].filter(
                    item => item !== value
                ),
            })
            return
        }
    }

    if (!isAnyFilterActive && !isAnalyzingSentiment) {
        return null
    }

    const resetAllLabel = `Reset all filters`
    return (
        <Box
            id="selected-filters"
            sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                minWidth: '280px',
                gap: 1,
                padding: '2px 0px',
                borderRadius: '4px',
            }}
        >
            {' '}
            {/* Reset Filters Button */}
            {activeFilterCount >= 2 && !disabled && (
                <Tooltip title={resetAllLabel}>
                    <IconButton
                        aria-label={resetAllLabel}
                        color="secondary"
                        onClick={resetAllFilters}
                        sx={{
                            p: 0,
                        }}
                    >
                        <DeleteOutlineIcon />
                    </IconButton>
                </Tooltip>
            )}
            {isAnalyzingSentiment && (
                <Chip
                    size="small"
                    key="sentiment-analyzing"
                    label="Analyzing sentiment"
                    deleteIcon={<CircularProgress size={14} color="inherit" />}
                    // onDelete is required for deleteIcon to render; no-op keeps
                    // the spinner in the delete slot without making it clickable.
                    onDelete={() => {}}
                    color="primary"
                    variant="outlined"
                />
            )}
            {!isAnalyzingSentiment &&
                recalculateFilters.sentiments &&
                recalculateFilters.sentiments.map(sentiment => (
                    <Chip
                        deleteIcon={<DeleteIcon />}
                        size="small"
                        key={`sentiment-${sentiment}`}
                        label={`Sentiment: ${sentiment}`}
                        onDelete={
                            disabled
                                ? null
                                : () =>
                                      handleDelete(
                                          'recalculate',
                                          'sentiments',
                                          sentiment
                                      )
                        }
                        color="primary"
                    />
                ))}
            {recalculateFilters.tags &&
                recalculateFilters.tags.map(tag => (
                    <Chip
                        deleteIcon={<DeleteIcon />}
                        size="small"
                        key={`tag-${tag}`}
                        label={`Tag: ${tag}`}
                        onDelete={
                            disabled
                                ? null
                                : () => handleDelete('recalculate', 'tags', tag)
                        }
                        color="secondary"
                    />
                ))}
            {recalculateFilters.topics &&
                recalculateFilters.topics.map(topic => {
                    // get loaded topics and insert topic info
                    // const topic = topics.find(
                    //     loadedTopic => loadedTopic.id === topicId
                    // )
                    const topicName = topic ? getTopicName(topic) : topic.id
                    return (
                        <Chip
                            deleteIcon={<DeleteIcon />}
                            size="small"
                            key={`topic-${topicName}`}
                            label={`Topic: ${topicName}`}
                            onDelete={
                                disabled
                                    ? null
                                    : () =>
                                          handleDelete(
                                              'recalculate',
                                              'topics',
                                              topic
                                          )
                            }
                            color="success"
                        />
                    )
                })}
            {highlightFilters.sentiments &&
                highlightFilters.sentiments.map(sentiment => (
                    <Chip
                        deleteIcon={<DeleteIcon />}
                        size="small"
                        key={`highlight-sentiment-${sentiment}`}
                        label={`Sentiment: ${sentiment}`}
                        onDelete={
                            disabled
                                ? null
                                : () =>
                                      handleDelete(
                                          'highlight',
                                          'sentiments',
                                          sentiment
                                      )
                        }
                        color="primary"
                    />
                ))}
            {highlightFilters.tags &&
                highlightFilters.tags.map(tag => (
                    <Chip
                        deleteIcon={<DeleteIcon />}
                        size="small"
                        key={`highlight-tag-${tag}`}
                        label={`Tag: ${tag}`}
                        onDelete={
                            disabled
                                ? null
                                : () => handleDelete('highlight', 'tags', tag)
                        }
                        color="secondary"
                    />
                ))}
            {highlightFilters.topics &&
                highlightFilters.topics.map(topic => {
                    // get loaded topics and insert topic info
                    // const topic = topics.find(
                    //     loadedTopic => loadedTopic.id === topicId
                    // )
                    const topicName = topic ? getTopicName(topic) : topic.id
                    return (
                        <Chip
                            deleteIcon={<DeleteIcon />}
                            size="small"
                            key={`highlight-topic-${topicName}`}
                            label={`Topic: ${topicName}`}
                            onDelete={
                                disabled
                                    ? null
                                    : () =>
                                          handleDelete(
                                              'highlight',
                                              'topics',
                                              topic
                                          )
                            }
                            color="success"
                        />
                    )
                })}
        </Box>
    )
}

export default SelectedFilters
